import './ContentBuilderTemplate.scss';
import './ContentBuilderTemplate80.scss';

import React, { useEffect, useMemo, useState } from 'react';
import { cn } from '../../../utils/bem';
import { usePropsHandler } from '../../EventInterceptor/usePropsHandler';
import { cnContentBuilderTemplate } from './index';
import { Typography } from '@dataartdev/uikit/Typography';
import { Grid, GridItem } from '@dataartdev/uikit/Grid';
import { forwardRefWithAs } from '../../../utils/types/PropsWithAsAttributes';
import { Mq } from '@dataartdev/uikit/Mq';
import { HorizontalVideoCard } from '../../HorizontalVideoCard/HorizontalVideoCard';
import { Button } from '@dataartdev/uikit/Button';
import { IconFilter } from '@dataartdev/uikit/IconFilter';
import { Combobox } from '@dataartdev/uikit/Combobox';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { FilterModal } from 'common/components';
import { IRoot80, TagItem, IndustriesItem } from './models/CTI_80.props';

interface ModalMethods {
  show: () => void;
  hide: () => void;
}

const cnFilteredCards = cn('FilteredCards');

export const ContentBuilderTemplate = forwardRefWithAs<IRoot80>(props => {
  const {
    type,
    contentId,
    title,
    // tags = [],
    tagItems = [],
    // industries = [],
    industriesItems = [],
    items = [],
    noContentText,
    noContentTitle,
  } = usePropsHandler(cnContentBuilderTemplate(), props);

  const typeNumber = type.toString();

  const history = useHistory();
  const location = useLocation();

  const { tags: tagsRaw, industries: indRaw } = queryString.parse(
    location.search
  );

  const tagsQuery = Array.isArray(tagsRaw) ? tagsRaw : tagsRaw ? [tagsRaw] : [];
  const industriesQuery = Array.isArray(indRaw)
    ? indRaw
    : indRaw
    ? [indRaw]
    : [];

  const [tagsSelected, setTagsSelected] = useState<TagItem[]>(() =>
    tagItems.filter(t => tagsQuery.includes(t.slug))
  );
  const [industriesSelected, setIndustriesSelected] = useState<
    IndustriesItem[]
  >(() => industriesItems.filter(i => industriesQuery.includes(i.slug)));

  const [modalTags, setModalTags] = useState<TagItem[]>([]);
  const [modalIndustries, setModalIndustries] = useState<IndustriesItem[]>([]);

  useEffect(() => {
    const filter: Record<string, string[] | undefined> = {};
    if (tagsSelected?.length) filter.tags = tagsSelected.map(t => t.slug);
    if (industriesSelected?.length)
      filter.industries = industriesSelected.map(i => i.slug);

    const newSearch = queryString.stringify(filter);
    const currentSearch = location.search.replace(/^\?/, '');

    if (newSearch !== currentSearch) {
      history.replace({ search: newSearch });
    }
  }, [tagsSelected, industriesSelected, history, location.search]);

  const filteredItems = useMemo(() => {
    return items.filter(item => {
      if (tagsSelected?.length > 0) {
        const itemTagSlugs = item.tagItems?.map(t => t.slug) ?? [];
        if (!tagsSelected.some(sel => itemTagSlugs.includes(sel.slug))) {
          return false;
        }
      }

      if (industriesSelected?.length > 0) {
        const itemIndSlugs = item.industriesItems?.map(i => i.slug) ?? [];
        if (!industriesSelected.some(sel => itemIndSlugs.includes(sel.slug))) {
          return false;
        }
      }
      return true;
    });
  }, [items, tagsSelected, industriesSelected]);

  const [visibleCount, setVisibleCount] = useState(5);

  useEffect(() => {
    setVisibleCount(5);
  }, [tagsSelected, industriesSelected]);

  const itemsToShow = filteredItems.slice(0, visibleCount);

  const clearAllHandler = () => {
    setIndustriesSelected([]);
    setTagsSelected([]);
  };

  /**
   * =========================== Filter Modal ===========================
   */

  const filterModalRef = React.useRef<ModalMethods>(null);

  const resetForm = () => {
    setIndustriesSelected([]);
    setTagsSelected([]);
    hideModalFilter();
  };

  const submitModalForm = () => {
    setTagsSelected(modalTags);
    setIndustriesSelected(modalIndustries);
    filterModalRef.current?.hide();
  };

  const showModalFilter = () => {
    setModalTags(tagsSelected);
    setModalIndustries(industriesSelected);
    filterModalRef.current?.show();
  };

  const hideModalFilter = () => {
    if (filterModalRef) {
      filterModalRef.current?.hide();
    }
  };

  return (
    <div
      className={cnContentBuilderTemplate({ type: typeNumber })}
      id={contentId}
    >
      <div className={cnFilteredCards()}>
        <Grid
          breakpoints={{
            desktop: { cols: 12, gap: '2xl' },
            tablet: { cols: 12, gap: '2xl' },
            mobile: { cols: 12, gap: '2xl' },
          }}
        >
          <GridItem
            breakpoints={{
              desktop: { col: 12 },
              tablet: { col: 12 },
              mobile: { col: 12 },
            }}
          >
            <Grid
              breakpoints={{
                desktop: { cols: 12, gap: 'xl' },
                tablet: { cols: 12, gap: 'xl' },
                mobile: { cols: 12, gap: 'xl' },
              }}
            >
              <GridItem
                breakpoints={{
                  desktop: { col: 12 },
                  tablet: { col: 12 },
                  mobile: { col: 12 },
                }}
                className={cnFilteredCards('Head')}
              >
                {title && (
                  <Typography.Title
                    size="lg"
                    as="h2"
                    parse
                    className={cnFilteredCards('Title')}
                  >
                    {title}
                  </Typography.Title>
                )}
                <Mq query="--tablet-max">
                  <Button
                    view="ghost"
                    iconRight={IconFilter}
                    onClick={showModalFilter}
                    className={cnFilteredCards('Filters-Mobile-Button')}
                  />
                  <FilterModal
                    ref={filterModalRef}
                    onSubmitForm={submitModalForm}
                    onResetForm={resetForm}
                    industries={{
                      title: 'industries section filter title',
                      contentItems: industriesItems,
                      value: modalIndustries,
                      onChange: ({ value }) => {
                        setModalIndustries(value);
                      },
                    }}
                    tags={{
                      title: 'tags section filter title',
                      contentItems: tagItems,
                      value: modalTags,
                      onChange: ({ value }) => {
                        setModalTags(value);
                      },
                    }}
                  />
                </Mq>
              </GridItem>
            </Grid>
          </GridItem>
          <GridItem
            breakpoints={{
              desktop: { col: 12 },
              tablet: { col: 12 },
              mobile: { col: 12 },
            }}
          >
            <Grid
              breakpoints={{
                desktop: { cols: 12, gap: 'xl' },
                tablet: { cols: 12, gap: 'xl' },
                mobile: { cols: 12, gap: 'xl' },
              }}
            >
              <Mq query="--desktop">
                <GridItem
                  breakpoints={{
                    desktop: { col: 3 },
                  }}
                >
                  <div className={cnFilteredCards('Filter-Selects')}>
                    {industriesItems?.length > 0 && (
                      <Combobox
                        multiple
                        placeholder="INDUSTRIES"
                        placeholderSelected="INDUSTRIES"
                        items={industriesItems}
                        value={
                          (industriesSelected as unknown) as IndustriesItem
                        }
                        getItemLabel={(item: IndustriesItem) =>
                          item.title ?? ''
                        }
                        onChange={({ value }) => {
                          setIndustriesSelected(value);
                        }}
                        view="outline"
                      />
                    )}
                    {tagItems?.length > 0 && (
                      <Combobox
                        multiple
                        placeholder="TAGS"
                        placeholderSelected="TAGS"
                        items={tagItems}
                        value={(tagsSelected as unknown) as TagItem}
                        onChange={({ value }) => {
                          setTagsSelected(value);
                        }}
                        getItemLabel={(item: TagItem) => item.title ?? ''}
                        view="outline"
                      />
                    )}
                  </div>
                  <Button
                    onClick={clearAllHandler}
                    view="primary"
                    label="Clear All"
                    className={cnFilteredCards('Clear')}
                  />
                </GridItem>
              </Mq>
              <GridItem
                breakpoints={{
                  desktop: { col: 9 },
                  tablet: { col: 12 },
                  mobile: { col: 12 },
                }}
              >
                <Grid
                  breakpoints={{
                    desktop: { cols: 12, gap: 'xl' },
                    tablet: { cols: 12, gap: 'xl' },
                    mobile: { cols: 12, gap: 'xl' },
                  }}
                >
                  {filteredItems.length === 0 &&
                    noContentTitle &&
                    noContentText && (
                      <GridItem
                        breakpoints={{
                          desktop: { col: 12 },
                          tablet: { col: 12 },
                          mobile: { col: 12 },
                        }}
                      >
                        <Typography.Title
                          className={cnFilteredCards('EmptyFilter-Title')}
                          size="lg"
                          as="h2"
                        >
                          {noContentTitle}
                          <br />
                          <span>{noContentText}</span>
                        </Typography.Title>
                      </GridItem>
                    )}
                  {itemsToShow?.length > 0 &&
                    itemsToShow.map(item => (
                      <GridItem
                        key={item.id}
                        breakpoints={{
                          desktop: { col: 12 },
                          tablet: { col: 12 },
                          mobile: { col: 12 },
                        }}
                      >
                        <HorizontalVideoCard {...item} />
                      </GridItem>
                    ))}
                  {visibleCount < filteredItems.length && (
                    <GridItem
                      breakpoints={{
                        desktop: { col: 12 },
                        tablet: { col: 12 },
                        mobile: { col: 12 },
                      }}
                    >
                      <Button
                        view="primary"
                        label="View More"
                        className={cnFilteredCards('More')}
                        onClick={() => setVisibleCount(prev => prev + 5)}
                      />
                    </GridItem>
                  )}
                </Grid>
              </GridItem>
            </Grid>
          </GridItem>
        </Grid>
      </div>
    </div>
  );
});

export default ContentBuilderTemplate;
